import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface DatawrapperChartRendererProps {
  chartId: string;
  className?: string;
}

/**
 * Renders a Datawrapper chart iframe with automatic height adjustment.
 * Listens for Datawrapper's postMessage height events for responsive sizing.
 * 
 * Charts render with NO rounded corners or cropping - clean embed.
 */
export default function DatawrapperChartRenderer({
  chartId,
  className = "media-default",
}: DatawrapperChartRendererProps) {
  const [height, setHeight] = useState(400);

  // Listen for Datawrapper's postMessage height updates
  useEffect(() => {
    if (!chartId) return;

    const handleMessage = (event: MessageEvent) => {
      const heights = event.data?.["datawrapper-height"];
      if (heights && heights[chartId]) {
        setHeight(heights[chartId]);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [chartId]);

  if (!chartId) {
    return null;
  }

  // Charts follow the same spacing as other media blocks (my-20 = 80px)
  // This is handled by .prose-article .datawrapper-chart in index.css
  return (
    <div className={cn("datawrapper-chart", className)}>
      <iframe
        src={`https://datawrapper.dwcdn.net/${chartId}/`}
        scrolling="no"
        frameBorder="0"
        style={{
          width: "100%",
          height: height,
          border: "none",
          display: "block",
        }}
        title="Datawrapper Chart"
        allowFullScreen
      />
    </div>
  );
}
